import React, { useMemo, useState } from 'react';
import RestbestandRow from '../models/RestbestandRow';
import Styles from './RestbestandSectionTable.module.scss';

type Props = {
  title: string;
  rows: RestbestandRow[];
  onBarcodeClick: (barcode: string, target: HTMLElement) => void;
};

type SortColumn = 'barcode' | 'status' | 'statusZeitpunkt';

// DMT-743 AC5/AC6: default sort is the Status-Zeitpunkt column, oldest first. Status-Zeitpunkt
// arrives pre-formatted as "dd.MM.yyyy HH:mm" (RestbestandDto.scala), so it is parsed chronologically
// below rather than string-compared (a plain string sort would be wrong, e.g. "01.02.2026" would sort
// before "15.01.2026" even though 15 Jan is chronologically earlier).
const DEFAULT_SORT_COLUMN: SortColumn = 'statusZeitpunkt';

/** Parses "dd.MM.yyyy HH:mm" into an epoch-ms number for chronological comparison; unparsable/null
 * values sort last (oldest-first default still puts "missing timestamp" rows at the bottom). */
function parseStatusZeitpunkt(value: string | null): number {
  if (!value) return Infinity;
  const match = /^(\d{2})\.(\d{2})\.(\d{4})\s+(\d{2}):(\d{2})$/.exec(value);
  if (!match) return Infinity;
  const [, day, month, year, hour, minute] = match;
  return new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute)).getTime();
}

const RestbestandSectionTable: React.FC<Props> = ({ title, rows, onBarcodeClick }) => {
  const [sortCol, setSortCol] = useState<SortColumn | null>(DEFAULT_SORT_COLUMN);
  const [sortAsc, setSortAsc] = useState<boolean>(true);

  const handleSort = (col: SortColumn) => {
    if (sortCol === col) {
      setSortAsc(!sortAsc);
    } else {
      setSortCol(col);
      setSortAsc(true);
    }
  };

  const sortedRows = useMemo(() => {
    if (!sortCol) return rows;
    return [...rows].sort((a, b) => {
      let cmp: number;
      if (sortCol === 'statusZeitpunkt') {
        cmp = parseStatusZeitpunkt(a.statusZeitpunkt) - parseStatusZeitpunkt(b.statusZeitpunkt);
      } else {
        const valA = (a[sortCol] as string | null) ?? '';
        const valB = (b[sortCol] as string | null) ?? '';
        cmp = valA.localeCompare(valB);
      }
      return sortAsc ? cmp : -cmp;
    });
  }, [rows, sortCol, sortAsc]);

  const sortIndicator = (col: SortColumn) => {
    if (sortCol !== col) return '';
    return sortAsc ? ' ▲' : ' ▼';
  };

  return (
    <section className={Styles.Section}>
      <h4 className={Styles.Title}>{title}</h4>
      <div className={Styles.TableContainer}>
        <table className={Styles.Table}>
          <thead>
            <tr>
              <th className={Styles.HeaderCell} onClick={() => handleSort('barcode')} title='Klicken zum Sortieren'>
                Barcode{sortIndicator('barcode')}
              </th>
              <th className={Styles.HeaderCell} onClick={() => handleSort('status')} title='Klicken zum Sortieren'>
                Status{sortIndicator('status')}
              </th>
              <th
                className={Styles.HeaderCell}
                onClick={() => handleSort('statusZeitpunkt')}
                title='Klicken zum Sortieren'>
                Status-Zeitpunkt{sortIndicator('statusZeitpunkt')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.length === 0 ? (
              <tr>
                <td colSpan={3} className={Styles.EmptyCell}>
                  Keine Sendungen im Restbestand vorhanden
                </td>
              </tr>
            ) : (
              sortedRows.map((row) => (
                <tr key={row.key()} className={Styles.Row}>
                  <td className={Styles.Cell}>
                    <button
                      type='button'
                      className={Styles.BarcodeLink}
                      onClick={(e) => onBarcodeClick(row.barcode, e.currentTarget)}>
                      {row.barcode}
                    </button>
                  </td>
                  <td className={Styles.Cell}>{row.status}</td>
                  <td className={Styles.Cell}>{row.statusZeitpunkt ?? '-'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className={Styles.Footer}>
        <span>Summe {rows.length}</span>
      </div>
    </section>
  );
};

export default RestbestandSectionTable;
